import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const AnomalyDetectorViewResult = ({result}) => {
  const formatArray = (arr) => {
    if (!arr) return '-';
    return arr.map(item => {
      if (typeof item === 'object') {
        // Handle complex finding objects
        if (item?.id && item?.description) {
          return (
            <div key={item?.id} className="mb-2 p-2 bg-base-200 rounded-lg">
              <div className="font-medium">{item?.id}: {item?.type}</div>
              <div className="text-sm mt-1">{item?.description}</div>
              {item?.severity && (
                <div className="mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    item?.severity === 'high' ? 'bg-error/20 text-error' :
                    item?.severity === 'medium' ? 'bg-warning/20 text-warning' :
                    'bg-info/20 text-info'
                  }`}>
                    Severity: {item.severity}
                  </span>
                  {item?.confidence && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      Confidence: {item?.confidence}%
                    </span>
                  )}
                </div>
              )}
              {item.impact && (
                <div className="text-sm mt-1 p-2">
                  <div>Impact: {item?.impact?.description}</div>
                  {item?.impact?.affected_areas && (
                    <div className="text-xs mt-0.5">
                      Affected areas: {item?.impact?.affected_areas?.join(', ')}
                    </div>
                  )}
                </div>
              )}
              {item?.location && (
                <div className="text-xs mt-1 text-base-content/70">
                  Location: {item?.location?.text}
                </div>
              )}
            </div>
          );
        }
        return JSON.stringify(item);
      }
      return item;
    });
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Anomaly Detection Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Warning size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Anomaly Detection</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Detection Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.anomalies?.detected ? 'bg-error/20 text-error' : 'bg-success/20 text-success'
                    }`}>
                      {result?.anomalies?.detected ? 'Anomalies Detected' : 'No Anomalies'}
                    </span>
                  </div>
                </div>

                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Anomaly Count</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">
                      {result?.anomalies?.count || 0}
                    </span>
                  </div>
                </div>

                {/* Pattern Anomalies */}
                <div className="space-y-2">
                  <h4 className="font-medium">Pattern Anomalies</h4>
                  {formatArray(result?.anomalies?.types?.pattern)}
                </div>

                {/* Content Anomalies */}
                <div className="space-y-2">
                  <h4 className="font-medium">Content Anomalies</h4>
                  {formatArray(result?.anomalies?.types?.contents)}
                </div>

                {/* Semantic Anomalies */}
                <div className="space-y-2">
                  <h4 className="font-medium">Semantic Anomalies</h4>
                  {formatArray(result?.anomalies?.types?.semantic)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Risk Assessment Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Risk Assessment</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Risk Level</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.risk_assessment?.level === 'high' ? 'bg-error/20 text-error' :
                      result?.risk_assessment?.level === 'medium' ? 'bg-warning/20 text-warning' :
                      'bg-success/20 text-success'
                    }`}>
                      {result?.risk_assessment?.level || 'Unknown'}
                    </span>
                  </div>
                </div>

                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Risk Score</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">
                      {result?.risk_assessment?.score || 0}
                    </span>
                  </div>
                </div>

                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Risk Confidence</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {result?.risk_assessment?.confidence ? `${result.risk_assessment.confidence}%` : '-'}
                    </span>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="space-y-2">
                  <h4 className="font-medium">Immediate Recommendations</h4>
                  {formatArray(result?.recommendations?.immediate)}
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Short Term Recommendations</h4>
                  {formatArray(result?.recommendations?.short_term)}
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Long Term Recommendations</h4>
                  {formatArray(result?.recommendations?.long_term)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnomalyDetectorViewResult;